import { Dispatch, SetStateAction, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

import {
  CONDITION_DEFINITIONS,
  CONDITION_OWNERS,
  conditionDefinitionsDescToIdMap,
  conditionOwnersDescToIdMap,
  SetSnackType,
  StudentConditionsNoNulls,
  StudentsNoNulls,
} from "../../utils";

export const AddStudentConditionDialog = (props: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  source: StudentsNoNulls;
  targets: StudentsNoNulls[];
  processStuCondAdd: (stuCond: StudentConditionsNoNulls) => Promise<boolean>;
}) => {
  const defaultDefinition = conditionDefinitionsDescToIdMap.get("Wish") ?? -1;
  const defaultOwner = conditionOwnersDescToIdMap.get("Student") ?? -1;

  const [addCondDef, setAddCondDef] = useState(defaultDefinition);
  const [addStuCondTarget, setAddStuCondTarget] = useState<StudentsNoNulls | null>(null);
  const [addStuCondTargetError, setAddStuCondTargetError] = useState(false);
  const [addCondOwner, setAddCondOwner] = useState(defaultOwner);

  const resetAddCond = () => {
    setAddCondDef(defaultDefinition);
    setAddStuCondTarget(null);
    setAddStuCondTargetError(false);
    setAddCondOwner(defaultOwner);
  };

  const validateAddStudentCondition = (stuCondToAdd: StudentConditionsNoNulls) => {
    if (!stuCondToAdd.target_id) {
      setAddStuCondTargetError(true);
      return false;
    }
    return true;
  };

  return (
    <Dialog open={props.open}>
      <DialogTitle>Add Student Condition</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Set the new student condition below
          <br />
          where the source student is {props.source.last}, {props.source.first}:
        </DialogContentText>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-stucond-def-label">Definition</InputLabel>
          <Select
            id="add-stucond-def"
            label="Definition"
            labelId="add-stucond-label"
            onChange={ev => setAddCondDef(ev.target.value)}
            value={addCondDef}
          >
            {CONDITION_DEFINITIONS.map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-stucond-target-label">Target</InputLabel>
          <Select
            id="add-stucond-target"
            error={addStuCondTargetError}
            label="Target"
            labelId="add-stucond-target-label"
            onChange={ev => setAddStuCondTargetError(false)}
            value={addStuCondTarget?.id ?? ""}
          >
            {props.targets.map(s => (
              <MenuItem
                key={s.id}
                value={s.id}
                selected={s.id === addStuCondTarget?.id}
                onClick={() => setAddStuCondTarget(s)}
              >
                {!s ? "" : `${s.last}, ${s.first}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-stucond-owner-label">Owner</InputLabel>
          <Select
            id="add-stucond-owner"
            label="Owner"
            labelId="add-stucond-owner-label"
            onChange={ev => setAddCondOwner(ev.target.value)}
            value={addCondOwner}
          >
            {CONDITION_OWNERS.filter(o => o.desc !== "SYSTEM").map(o => (
              <MenuItem key={o.id} value={o.id}>
                {o.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.setOpen(false);
            resetAddCond();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            const stuCondToAdd = {
              source_id: props.source.id,
              definition_id: addCondDef,
              target_id: addStuCondTarget?.id,
              owner_id: addCondOwner,
            } as StudentConditionsNoNulls;
            if (!validateAddStudentCondition(stuCondToAdd)) {
              setAddStuCondTargetError(true);
              props.setSnack("Unable to add student condition, check invalid fields", "warning");
              return;
            }
            if (await props.processStuCondAdd(stuCondToAdd)) {
              props.setOpen(false);
              resetAddCond();
            }
          }}
          variant="contained"
        >
          Add Student Condition
        </Button>
      </DialogActions>
    </Dialog>
  );
};
